<script setup>
import { onMounted, computed, ref, toRefs, provide } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { participantAvatarUrl } from 'dashboard/composables/useParticipantAvatars';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';
import BrainIcon from 'dashboard/components-next/icon/BrainIcon.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
import StickerBubble from './bubbles/Sticker.vue';
import WhatsAppTemplateBubble from './bubbles/Template/WhatsAppPreview.vue';

import MessageError from './MessageError.vue';
import MessageMeta from './MessageMeta.vue';
import MessageReactions from 'dashboard/components/widgets/conversation/MessageReactions.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import BotResponseRating from './BotResponseRating.vue';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const currentUserGetter = useMapGetter('getCurrentUser');
const authToken = computed(() => currentUserGetter.value?.access_token || '');
const selectedChatGetter = useMapGetter('getSelectedChat');

const resolvedSenderJid = computed(() => {
  const ca = props.contentAttributes || {};
  return ca.sender_jid || ca.senderJid || null;
});

const resolvedPushName = computed(() => {
  const ca = props.contentAttributes || {};
  return ca.push_name || ca.pushName || null;
});

const resolvedIsGroupMessage = computed(() => {
  const ca = props.contentAttributes || {};
  const v = ca.is_group_message;
  if (v === true || v === false) return v;
  const v2 = ca.isGroupMessage;
  if (v2 === true || v2 === false) return v2;
  return false;
});

const groupSenderAvatarUrl = computed(() => {
  const rawJid = resolvedSenderJid.value;

  // Fallback: se props.inboxId nao vier (problema conhecido com v-bind="message"
  // que nao mapeia todos os campos snake_case automaticamente), pega do store
  // via currentChat.inbox_id
  const currentChat = selectedChatGetter.value;
  const inboxId = props.inboxId || currentChat?.inbox_id;

  if (!rawJid || !inboxId || !authToken.value) return '';

  // Strip device suffix (ex: 556181904268:44@s.whatsapp.net -> 556181904268@s.whatsapp.net)
  const normalizedJid = rawJid.replace(/:\d+@/, '@');
  const match = window.location.pathname.match(/\/app\/accounts\/(\d+)/);
  const acctId = match ? match[1] : '';
  const pumaFallback = `/api/v1/accounts/${acctId}/inboxes/${inboxId}/dinastia_whatsapp/participant_avatar?jid=${encodeURIComponent(normalizedJid)}&api_access_token=${authToken.value}`;
  // Estatica via nginx quando materializada (1 manifest em lote por tela);
  // endpoint antigo so enquanto o arquivo ainda nao existe.
  return participantAvatarUrl(inboxId, normalizedJid, pumaFallback);
});

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.EXTERNAL_ECHO;
  }

  const isBot =
    !props.sender ||
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      props.sender.type
    );
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  // Template de bot/sistema → automated (azul). Excecao: template OFICIAL
  // do WhatsApp (tem templateParams) usa o mesmo balao do avulso/disparo,
  // seja quem for o remetente — padrao pedido pela dona (12/09).
  const isWhatsAppTemplate = !!props.additionalAttributes?.templateParams?.name;
  if (
    isBot &&
    props.messageType === MESSAGE_TYPES.TEMPLATE &&
    !isWhatsAppTemplate
  ) {
    return MESSAGE_VARIANTS.AUTOMATED;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const showGroupHeader = computed(() => {
  if (!resolvedIsGroupMessage.value) return false;
  if (!resolvedPushName.value) return false;
  // Use messageType directly instead of orientation enum (more robust):
  // incoming messages = 0 (numeric) or 'incoming' (string)
  const type = props.messageType;
  return type === 0 || type === MESSAGE_TYPES.INCOMING || type === 'incoming';
});

// Provide group header data so the inner BaseBubble can render it.
// Vue 3 does not forward named slots through intermediate components,
// so we use provide/inject to skip the bubble layer entirely.
const groupHeaderData = computed(() => {
  if (!showGroupHeader.value) return null;
  return {
    senderJid: resolvedSenderJid.value,
    avatarUrl: groupSenderAvatarUrl.value,
    displayName: props.sender?.name || resolvedPushName.value,
  };
});

provide('groupHeaderData', groupHeaderData);

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowMeta = computed(() => {
  if (shouldGroupWithNext.value) return false;
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) return false;
  return true;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (props.contentAttributes?.type === 'sticker') return StickerBubble;

  // Template oficial do WhatsApp com metadados → renderiza visual completo
  // (cabecalho/imagem, rodape, botoes). Vale para envios gravados como
  // template (tipo 3) ou outgoing (tipo 1). Sem metadados cai no balao de texto.
  if (props.additionalAttributes?.templateParams?.name) {
    return WhatsAppTemplateBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const isDinastiaWhatsapp = computed(() => {
  return inbox.value?.channel_type === INBOX_TYPES.DINASTIA_WHATSAPP;
});

const messageReactions = computed(() => {
  return props.contentAttributes?.reactions || [];
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    react:
      isDinastiaWhatsapp.value &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    editMessage:
      isDinastiaWhatsapp.value &&
      isOutgoing &&
      hasText &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    deleteForAll:
      isDinastiaWhatsapp.value &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender → automated/bot message
  if (!props.sender) {
    // Template messages from system → Brain branding
    if (props.messageType === MESSAGE_TYPES.TEMPLATE) {
      return {
        name: 'Brain',
        src: '',
        iconName: 'i-lucide-brain',
      };
    }
    return {
      name: 'Brain',
      src: '',
    };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const isBroadcastMessage = computed(() => {
  return !!props.additionalAttributes?.campaignId;
});

// 👍/👎 do bot: so respostas do Captain (nunca AgentBot generico, humano ou
// deletada) — aparece no hover da bolha, avaliacao 100% opcional
const showBotRating = computed(() => {
  const senderType = props.sender?.type ?? props.senderType;
  return (
    senderType === SENDER_TYPES.CAPTAIN_ASSISTANT &&
    !isMessageDeleted.value &&
    !props.private
  );
});

const isBrainAvatar = computed(() => {
  const v = variant.value;
  if (v === MESSAGE_VARIANTS.BOT || v === MESSAGE_VARIANTS.AUTOMATED) {
    return true;
  }
  if (!props.sender) return false;
  const { type } = props.sender;
  return [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
    type
  );
});

const brainVariant = computed(() => {
  return 'brand';
});

// Mensagem de automacao (fluxo/script) sem remetente: logo do Brain no
// lugar do avatar, com "Sistema de automacoes Brain" no hover. Bot de
// verdade (Captain/AgentBot) continua com o sparkle e o nome dele.
const isAutomationSystem = computed(() => {
  if (props.sender) return false;
  const v = variant.value;
  return (
    v === MESSAGE_VARIANTS.BOT ||
    v === MESSAGE_VARIANTS.AUTOMATED ||
    v === MESSAGE_VARIANTS.TEMPLATE
  );
});

// Mensagem que nasceu de um agendamento (carimbo do ScheduledMessages::SendService).
// As chaves chegam em camelCase pela lista, mas cruas em outros caminhos.
const isScheduledMessage = computed(() => {
  const ca = props.contentAttributes || {};
  return Boolean(ca.scheduledMessageId || ca.scheduled_message_id);
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.sent_from_phone) {
    return t('CONVERSATION.SENT_FROM_PHONE');
  }
  if (props.contentAttributes?.externalEcho) {
    return t('CONVERSATION.NATIVE_APP_ADVISORY');
  }
  if (isAutomationSystem.value) {
    return t('CONVERSATION.AUTOMATION_SYSTEM');
  }
  if (isBrainAvatar.value) {
    const botName = props.sender?.name || 'Brain';
    return `${t('CONVERSATION.SENT_BY')} ${botName}`;
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full mb-[10px] message-bubble-container group/msg"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <BrainIcon
          v-if="isAutomationSystem || isBrainAvatar"
          :size="24"
          :variant="brainVariant"
          class="rounded-lg"
        />
        <Avatar v-else v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <BotResponseRating
          v-if="showBotRating"
          :message-id="props.id"
          class="opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 transition-opacity mr-1"
        />
        <div class="flex flex-col">
          <span
            v-if="isBroadcastMessage && !isMessageDeleted"
            class="text-[10px] font-medium flex items-center gap-0.5 mb-1 px-1.5 py-0.5 rounded-md bg-n-violet-3 text-n-violet-11 w-fit"
            :class="{ 'ml-auto': orientation === ORIENTATION.RIGHT }"
          >
            <Icon icon="i-lucide-megaphone" class="size-2.5" />
            {{ t('CONVERSATION.BROADCAST_BADGE') }}
          </span>
          <span
            v-if="contentAttributes.forwarded && !isMessageDeleted"
            class="text-xs text-n-slate-10 flex items-center gap-1 mb-1"
          >
            <Icon icon="i-lucide-forward" class="size-3" />
            {{ t('CONVERSATION.FORWARDED_MESSAGE') }}
          </span>
          <span
            v-if="isScheduledMessage && !isMessageDeleted"
            class="text-[10px] font-medium flex items-center gap-0.5 mb-1 px-1.5 py-0.5 rounded-md bg-n-amber-3 text-n-amber-11 w-fit"
            :class="{ 'ml-auto': orientation === ORIENTATION.RIGHT }"
          >
            <Icon icon="i-lucide-calendar-clock" class="size-2.5" />
            {{ t('CONVERSATION.SCHEDULED_BADGE') }}
          </span>
          <div
            v-if="isMessageDeleted"
            class="px-[13px] py-[9px] rounded-2xl italic text-n-slate-10 bg-n-alpha-2"
            :class="{
              'ltr:rounded-tr-sm rtl:rounded-tl-sm right-bubble':
                orientation === ORIENTATION.RIGHT,
              'ltr:rounded-tl-sm rtl:rounded-tr-sm left-bubble':
                orientation === ORIENTATION.LEFT,
            }"
          >
            <span class="flex items-center gap-1.5 text-xs">
              <Icon icon="i-lucide-ban" class="size-3.5" />
              {{ t('CONVERSATION.DINASTIA_ACTIONS.MESSAGE_DELETED') }}
            </span>
          </div>
          <Component :is="componentToRender" v-else />
          <MessageReactions
            v-if="messageReactions.length > 0 && !isMessageDeleted"
            :reactions="messageReactions"
          />
        </div>
      </div>
      <div
        v-if="shouldShowMeta || contentAttributes.externalError"
        class="[grid-area:meta] flex flex-col gap-1"
        :class="[
          orientation === ORIENTATION.RIGHT
            ? 'items-end ltr:mr-8 rtl:ml-8'
            : 'items-start',
        ]"
      >
        <MessageMeta v-if="shouldShowMeta" class="mt-[3px] text-n-stone-300" />
        <MessageError
          v-if="contentAttributes.externalError"
          :error="contentAttributes.externalError"
          @retry="emit('retry')"
        />
      </div>
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}
</style>
